import React, { useState, useEffect } from 'react';
import {
  Send, Eye, Trash2, RefreshCw, Users, CheckCircle2, XCircle,
  Clock, AlertTriangle, X, Loader2, FileText, RotateCcw, CheckSquare, Square, Search, DollarSign, Download, Play
} from 'lucide-react';
import { getCampaigns, deleteCampaign, getCampaignRecipients, getApprovedTemplates, resendCampaign, getCampaignEstimatedCost, getSectors } from '../services/api';
import api from '../services/api';

const statusInfo = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700', icon: Clock },
  scheduled: { label: 'Agendada', color: 'bg-purple-100 text-purple-700', icon: Clock },
  sending: { label: 'Enviando', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completed: { label: 'Concluída', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  failed: { label: 'Falhou', color: 'bg-red-100 text-red-700', icon: XCircle },
  paused: { label: 'Pausada (taxa de falha alta)', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle }
};

// ⭐ NOVO: traduz os erros mais comuns da Meta pra uma explicação simples.
// Se não reconhecer o erro, mostra o texto original mesmo (nunca esconde a informação).
function explainError(rawError) {
  if (!rawError) return null;
  const err = rawError.toLowerCase();

  if (err.includes('authentication error') || err.includes('oauthexception')) {
    return 'O acesso do sistema à Meta expirou ou foi revogado. É preciso gerar um novo token de acesso.';
  }
  if (err.includes('131009')) {
    return 'O número de telefone desse contato está incompleto ou inválido (poucos dígitos). Confira e corrija o número na planilha.';
  }
  if (err.includes('132001')) {
    return 'Esse template não foi encontrado aprovado nesse idioma na Meta. Ele pode ter sido apagado, renomeado, ou ainda não foi sincronizado no sistema.';
  }
  if (err.includes('132012')) {
    return 'O conteúdo enviado não bate com o formato exigido pelo template aprovado (ex: faltou uma variável, ou o cabeçalho não é do tipo esperado).';
  }
  if (err.includes('132000')) {
    return 'Faltou preencher alguma variável obrigatória do template (ex: nome do cliente).';
  }
  if (err.includes('470')) {
    return 'A janela de 24 horas com esse cliente expirou, e a mensagem não era um template aprovado.';
  }
  if (err.includes('nenhum pdf conferido')) {
    return 'Não foi encontrado o boleto (PDF) certo pra esse cliente. Confira se ele foi enviado e conferido corretamente.';
  }
  if (err.includes('não processado')) {
    return 'O envio foi interrompido antes de chegar nesse contato (ex: o sistema reiniciou no meio do disparo). Pode tentar reenviar.';
  }

  if (err.includes('spam rate limit')) {
    return 'A Meta restringiu a quantidade de mensagens deste número de telefone (mensagens anteriores foram bloqueadas ou marcadas como spam). Não é um problema deste contato — dá para reenviar depois que o número voltar a entregar.';
  }
  if (err.includes('healthy ecosystem')) {
    return 'Limite da Meta por destinatário: este contato já recebeu muitas mensagens de marketing de várias empresas. A Meta orienta esperar pelo menos 24 horas antes de tentar de novo.';
  }
  if (err.includes('undeliverable')) {
    return 'A Meta não conseguiu entregar a mensagem. O número provavelmente não tem WhatsApp ativo — reenviar tende a falhar de novo.';
  }

  // Não reconhecido — mostra o texto original mesmo
  return rawError;
}

// ⭐ NOVO: data/hora no horário de Brasília (o banco guarda em UTC)
function fmtDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

// ⭐ NOVO: rótulo curto do motivo da falha, pro resumo no topo da janela
function reasonLabel(rawError) {
  if (!rawError) return 'Sem motivo registrado';
  const err = rawError.toLowerCase();
  if (err.includes('spam rate limit')) return 'Número limitado pela Meta (spam)';
  if (err.includes('healthy ecosystem')) return 'Limite da Meta por destinatário';
  if (err.includes('undeliverable')) return 'Mensagem não entregue';
  if (err.includes('nenhum pdf conferido')) return 'Sem boleto conferido';
  if (err.includes('não processado')) return 'Interrompido antes de enviar';
  return rawError.length > 45 ? rawError.slice(0, 45) + '…' : rawError;
}

// ⭐ NOVO: modal de seleção de template pra reenviar os destinatários com erro
const ResendTemplateModal = ({ campaignId, recipientIds, onClose, onResent }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getApprovedTemplates();
        setTemplates(res.data || []);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar os templates aprovados.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleResend = async (templateId) => {
    setSendingId(templateId);
    try {
      const res = await resendCampaign(campaignId, {
        recipient_ids: recipientIds,
        template_id: templateId
      });
      alert(`✅ ${res.data.message}`);
      onResent();
    } catch (err) {
      alert('Erro ao reenviar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <RotateCcw size={20} className="text-green-600" />
            Reenviar com outro template
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <p className="text-xs text-gray-500 dark:text-slate-400 mb-4">
          Você está reenviando para <strong>{recipientIds.length}</strong> destinatário(s) que falharam. Escolha o template aprovado que será usado no reenvio.
        </p>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-green-600" />
            </div>
          )}

          {!loading && error && (
            <p className="text-sm text-red-500 text-center py-4">{error}</p>
          )}

          {!loading && !error && templates.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum template aprovado encontrado.</p>
          )}

          {!loading && templates.map(tpl => (
            <div key={tpl.id} className="border border-gray-200 dark:border-slate-600 rounded-xl p-3 hover:border-green-400 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm text-gray-900 dark:text-slate-100">
                  {tpl.display_name || tpl.name}
                </span>
                <span className="text-[10px] px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full font-medium">
                  {tpl.category || 'Utility'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-2 mb-2 whitespace-pre-wrap">
                {(tpl.components || []).find(c => c.type === 'BODY')?.text || 'Sem texto'}
              </p>
              <button
                onClick={() => handleResend(tpl.id)}
                disabled={sendingId !== null}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {sendingId === tpl.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sendingId === tpl.id ? 'Reenviando...' : 'Usar este template'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [costData, setCostData] = useState(null);
  const [loadingCost, setLoadingCost] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set()); // ⭐ NOVO
  const [showResendModal, setShowResendModal] = useState(false); // ⭐ NOVO
  const [search, setSearch] = useState(''); // ⭐ NOVO: busca por nome da campanha
  const [sectors, setSectors] = useState([]); // ⭐ NOVO: filtro por abas de setor
  const [activeSectorTab, setActiveSectorTab] = useState(null);

  useEffect(() => {
    getSectors().then(res => {
      setSectors(res.data);
      if (res.data.length > 0) setActiveSectorTab(res.data[0].id);
    }).catch(err => console.error('Erro ao buscar setores', err));
    loadCampaigns();
    const interval = setInterval(loadCampaigns, 10000); // atualiza sozinho a cada 10s
    return () => clearInterval(interval);
  }, []);

  const loadCampaigns = async () => {
    try {
      const res = await getCampaigns();
      setCampaigns(res.data);
    } catch (err) {
      console.error('Erro ao buscar campanhas', err);
    } finally {
      setLoading(false);
    }
  };

    const openDetails = async (campaign) => {
    setSelectedCampaign(campaign);
    setRecipientFilter('all');
    setSelectedIds(new Set());
    await loadRecipients(campaign.id, null);
  };

  const openCost = async (campaign) => {
    setLoadingCost(true);
    setCostData({ campaign_name: campaign.name });
    try {
      const res = await getCampaignEstimatedCost(campaign.id);
      setCostData({ campaign_name: campaign.name, ...res.data });
    } catch (err) {
      alert('Erro ao calcular custo: ' + (err.response?.data?.error || err.message));
      setCostData(null);
    } finally {
      setLoadingCost(false);
    }
  };

  const loadRecipients = async (campaignId, status) => {
    setLoadingRecipients(true);
    try {
      const res = await getCampaignRecipients(campaignId, status);
      setRecipients(res.data);
    } catch (err) {
      console.error('Erro ao buscar destinatários', err);
    } finally {
      setLoadingRecipients(false);
    }
  };
  // ⭐ NOVO: exporta TODOS os destinatários (sem filtro de aba) pra Excel/CSV,
  // com Status pra poder filtrar falha/entregue/enviado/lido depois no Excel
  const [exportingRecipients, setExportingRecipients] = useState(false);
  const exportRecipientsToExcel = async () => {
    if (!selectedCampaign) return;
    setExportingRecipients(true);
    try {
      const res = await getCampaignRecipients(selectedCampaign.id, null);
      const rows = res.data || [];
      const statusLabels = {
        sent: 'Enviado',
        delivered: 'Entregue',
        read: 'Lido',
        failed: 'Falhou',
        pending: 'Pendente'
      };
      const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
      // NOVO: formata o CPF (vem salvo como contact_cpf) no padrao 000.000.000-00
      // quando tiver os 11 digitos; senao mostra como veio, sem travar a exportacao
      const formatCpf = (cpf) => {
        if (!cpf) return '';
        const digits = String(cpf).replace(/\D/g, '');
        if (digits.length === 11) {
          return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9,11)}`;
        }
        return cpf;
      };
      const header = ['Nome', 'CPF', 'DDD do Pais', 'Telefone', 'Status', 'Erro'];
      const lines = [header.join(';')];
      rows.forEach(r => {
        const digits = String(r.contact_wa_id || '').replace(/\D/g, '');
        const ddd = digits.slice(0, 2);
        const phone = digits.slice(2);
        const statusLabel = statusLabels[r.status] || r.status || '';
        const erro = r.status === 'failed' ? (r.error_message || '') : '';
        lines.push([
          escape(r.contact_name || 'Sem nome'),
          escape(formatCpf(r.contact_cpf)),
          escape(ddd),
          escape(phone),
          escape(statusLabel),
          escape(erro)
        ].join(';'));
      });
      const csvContent = '\uFEFF' + lines.join('\r\n'); // BOM garante acento certo no Excel
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (selectedCampaign.name || 'campanha').replace(/[^a-zA-Z0-9_\-]+/g, '_');
      link.download = `${safeName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao exportar: ' + (err.response?.data?.error || err.message));
    } finally {
      setExportingRecipients(false);
    }
  };

  const handleFilterChange = (status) => {
    setRecipientFilter(status);
    setSelectedIds(new Set()); // ⭐ limpa seleção ao trocar de filtro
    loadRecipients(selectedCampaign.id, status === 'all' ? null : status);
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Excluir a campanha "${campaign.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteCampaign(campaign.id);
      loadCampaigns();
    } catch (err) {
      alert('Erro ao excluir campanha: ' + (err.response?.data?.error || err.message));
    }
  };
  // ⭐ NOVO: cancela o agendamento (volta a campanha pra rascunho)
  const handleCancelSchedule = async (campaign) => {
    if (!window.confirm(`Cancelar o agendamento da campanha "${campaign.name}"?`)) return;
    try {
      await api.post(`/api/campaigns/${campaign.id}/cancel-schedule`);
      loadCampaigns();
    } catch (err) {
      alert('Erro ao cancelar agendamento: ' + (err.response?.data?.error || err.message));
    }
  };

  // ⭐ NOVO: retoma uma campanha pausada automaticamente por taxa de falha alta —
  // continua só pros destinatários que ainda não foram tentados
  const handleResume = async (campaign) => {
    if (!window.confirm(`Retomar o envio da campanha "${campaign.name}"? Ela foi pausada automaticamente por taxa de falha alta — confira os destinatários antes de continuar.`)) return;
    try {
      await api.post(`/api/campaigns/${campaign.id}/launch`);
      loadCampaigns();
    } catch (err) {
      alert('Erro ao retomar campanha: ' + (err.response?.data?.error || err.message));
    }
  };

  // ⭐ NOVO: seleção individual
  const toggleSelect = (recipientId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(recipientId)) next.delete(recipientId);
      else next.add(recipientId);
      return next;
    });
  };

  // ⭐ NOVO: selecionar/desmarcar todos os visíveis (que já estão filtrados por "failed")
  const toggleSelectAll = () => {
    const allIds = recipients.map(r => r.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  // ⭐ NOVO: após reenvio, fecha modal de template e recarrega tudo
  const handleResent = async () => {
    setShowResendModal(false);
    setSelectedIds(new Set());
    await loadRecipients(selectedCampaign.id, recipientFilter === 'all' ? null : recipientFilter);
    loadCampaigns();
  };

  const recipientCounts = {
    all: recipients.length,
    failed: recipients.filter(r => r.status === 'failed').length,
  };

  const allVisibleSelected = recipients.length > 0 && recipients.every(r => selectedIds.has(r.id));

  const filteredCampaigns = campaigns.filter(c =>
    (activeSectorTab == null || c.sector_id === activeSectorTab) &&
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Campanhas</h1>
          <p className="text-gray-500 dark:text-slate-400">Acompanhe o status e os erros dos disparos em massa</p>
        </div>
        <button
          onClick={loadCampaigns}
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
        >
          <RefreshCw size={16} />
          Atualizar
        </button>
      </div>

      {/* ⭐ NOVO: abas de filtro por setor, estilo "aba de pasta" */}
      {sectors.length > 0 && (
        <div className="flex gap-2 flex-wrap -mb-px">
          {sectors.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSectorTab(s.id)}
              className={`px-6 py-2.5 text-base font-bold rounded-t-lg border transition-colors ${
                activeSectorTab === s.id
                  ? 'bg-white dark:bg-slate-800 border-blue-400 dark:border-blue-500 border-b-white dark:border-b-slate-800 text-gray-900 dark:text-slate-100 z-10 relative'
                  : 'bg-transparent border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={18} />
        <input
          type="text"
          placeholder="Buscar campanha pelo nome..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
        />
        </div>
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left table-fixed">
            <thead className="bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[18%]">Campanha</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[20%]">Template</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[10%]">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[10%]">Destinatários</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[9%]">Enviadas</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[9%]">Falhas</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-[12%]">Criada em</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider text-center w-[12%]">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Send size={48} className="mx-auto text-gray-300 dark:text-slate-600 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium">Nenhuma campanha criada ainda</p>
                  </td>
                </tr>
             ) : (
                filteredCampaigns.map(c => {
                  const info = statusInfo[c.status] || statusInfo.draft;
                  const StatusIcon = info.icon;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-slate-100 truncate" title={c.name}>{c.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400 truncate" title={c.template_name || '-'}>{c.template_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${info.color}`}>
                          <StatusIcon size={12} className={c.status === 'sending' ? 'animate-spin' : ''} />
                          {info.label}
                        </span>
                        {/* ⭐ NOVO: mostra a data/hora do agendamento */}
                        {c.status === 'scheduled' && c.scheduled_at && (
                          <p className="text-[10px] text-purple-600 dark:text-purple-400 mt-1">
                            {new Date(c.scheduled_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">{c.total_recipients || 0}</td>
                      <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 font-medium">{c.sent_count || 0}</td>
                      <td className="px-4 py-3 text-sm">
                        {c.status === 'completed' || c.status === 'failed' ? (
                          <span className={(c.failed_count || 0) > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400'}>
                            {c.failed_count || 0}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">
                        {new Date(c.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                                                    <button
                            onClick={() => openDetails(c)}
                            className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="Ver detalhes e erros"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => openCost(c)}
                            className="p-2 text-gray-400 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                            title="Custo estimado"
                          >
                            <DollarSign size={18} />
                          </button>
                          {/* ⭐ NOVO: retomar — só aparece pra campanhas pausadas automaticamente */}
                          {c.status === 'paused' && (
                            <button
                              onClick={() => handleResume(c)}
                              className="p-2 text-gray-400 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              title="Retomar envio"
                            >
                              <Play size={18} />
                            </button>
                          )}
                          {/* ⭐ NOVO: cancelar agendamento — só aparece pra campanhas agendadas */}
                          {c.status === 'scheduled' && (
                            <button
                              onClick={() => handleCancelSchedule(c)}
                              className="p-2 text-gray-400 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                              title="Cancelar agendamento"
                            >
                              <XCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(c)}
                            className="p-2 text-gray-400 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-xl">
            <div className="p-6 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">{selectedCampaign.name}</h2>
                <p className="text-sm text-gray-500 dark:text-slate-400">Template: {selectedCampaign.template_name || '-'}</p>
                {recipientFilter === 'all' && recipients.length > 0 && (() => {
                  const pend = recipients.filter(r => r.status === 'pending').length;
                  const ultima = recipients.reduce((max, r) => (r.sent_at && (!max || new Date(r.sent_at) > new Date(max)) ? r.sent_at : max), null);
                  // ⭐ NOVO: campanha com padrão de intervalos mostra a média, não o fixo
                  const padrao = Array.isArray(selectedCampaign.send_delay_pattern) && selectedCampaign.send_delay_pattern.length > 0 ? selectedCampaign.send_delay_pattern : null;
                  const delayS = padrao
                    ? (padrao.reduce((a, b) => a + b, 0) / padrao.length) / 1000
                    : (selectedCampaign.send_delay_ms ? selectedCampaign.send_delay_ms / 1000 : null);
                  return (
                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                      {delayS ? `${padrao ? `Intervalos: padrão de ${padrao.length} tempos (média ${Math.round(delayS * 10) / 10}s)` : `Intervalo: ${delayS}s`} · ` : ''}
                      {ultima ? `Última tentativa: ${fmtDateTime(ultima)} · ` : ''}
                      {`Pendentes: ${pend}`}
                      {selectedCampaign.status === 'sending' && delayS && pend > 0 ? ` · ${padrao ? 'previsão' : 'previsão mínima'}: ~${Math.ceil(pend * delayS / 60)} min` : ''}
                    </p>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={exportRecipientsToExcel}
                  disabled={exportingRecipients}
                  className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                >
                  {exportingRecipients ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  Exportar Excel
                </button>
                <button
                  onClick={() => setSelectedCampaign(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 dark:border-slate-700 flex gap-2 flex-shrink-0 flex-wrap items-center">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  recipientFilter === 'all' ? 'bg-gray-900 dark:bg-slate-100 text-white dark:text-slate-900' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                }`}
              >
                Todos ({recipients.length})
              </button>
              <button
                onClick={() => handleFilterChange('failed')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  recipientFilter === 'failed' ? 'bg-red-600 text-white' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                }`}
              >
                <AlertTriangle size={14} />
                Com erro ({recipientCounts.failed})
              </button>
              <button
                onClick={() => handleFilterChange('sent')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  recipientFilter === 'sent' ? 'bg-green-600 text-white' : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                }`}
              >
                Enviados
              </button>
              <button
                onClick={() => handleFilterChange('pending')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  recipientFilter === 'pending' ? 'bg-blue-600 text-white' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                }`}
              >
                Pendentes
              </button>

              {/* ⭐ NOVO: só aparece na aba "Com erro" */}
              {recipientFilter === 'failed' && recipients.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {allVisibleSelected ? <CheckSquare size={16} className="text-green-600" /> : <Square size={16} />}
                  Selecionar todos
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {recipients.some(r => r.status === 'failed') && (() => {
                const grupos = {};
                recipients.filter(r => r.status === 'failed').forEach(r => {
                  const k = reasonLabel(r.error_message);
                  grupos[k] = (grupos[k] || 0) + 1;
                });
                return (
                  <div className="mb-3 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30">
                    <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Motivos das falhas</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {Object.entries(grupos).sort((a, b) => b[1] - a[1]).map(([motivo, qtd]) => (
                        <span key={motivo} className="text-xs text-red-600 dark:text-red-400">{qtd} · {motivo}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {loadingRecipients ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : recipients.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={40} className="mx-auto text-gray-300 dark:text-slate-600 mb-2" />
                  <p className="text-gray-500 dark:text-slate-400">Nenhum destinatário nessa categoria</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recipients.map(r => (
                    <div
                      key={r.id}
                      className={`p-3 rounded-xl border flex items-start gap-3 ${
                        r.status === 'failed'
                          ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30'
                          : 'bg-gray-50 dark:bg-slate-900/50 border-gray-100 dark:border-slate-700'
                      }`}
                    >
                      {/* ⭐ NOVO: checkbox só na aba "Com erro" */}
                      {recipientFilter === 'failed' && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          className="mt-1 w-4 h-4 accent-green-600 flex-shrink-0 cursor-pointer"
                        />
                      )}
                      <div className="flex-1 flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-slate-100 text-sm">
                            {r.contact_name || 'Sem nome'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">{r.contact_wa_id}</p>
                          {(r.sent_at || r.delivered_at || r.read_at || r.pdf_filename || r.wa_message_id) && (
                            <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400 space-y-0.5">
                              {r.sent_at && <p>{r.status === 'failed' ? 'Tentativa' : 'Enviado'}: {fmtDateTime(r.sent_at)}</p>}
                              {r.delivered_at && <p>Entregue: {fmtDateTime(r.delivered_at)}</p>}
                              {r.read_at && <p>Lido: {fmtDateTime(r.read_at)}</p>}
                              {r.pdf_filename && <p className="break-all">Boleto: {r.pdf_filename}</p>}
                              {r.wa_message_id && <p className="font-mono break-all select-all">ID Meta: {r.wa_message_id}</p>}
                            </div>
                          )}
                                                    {r.status === 'failed' && r.error_message && (
                            <div className="mt-1">
                              <p className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                                {explainError(r.error_message)}
                              </p>
                              {explainError(r.error_message) !== r.error_message && (
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 ml-4 font-mono">
                                  {r.error_message}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-bold ${
                          r.status === 'sent' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          r.status === 'delivered' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                          r.status === 'read' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' :
                          r.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                        }`}>
                          {r.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ⭐ NOVO: barra flutuante de ação quando há itens selecionados */}
            {recipientFilter === 'failed' && selectedIds.size > 0 && (
              <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between bg-gray-50 dark:bg-slate-900 flex-shrink-0">
                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                  {selectedIds.size} selecionado(s)
                </span>
                <button
                  onClick={() => setShowResendModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition-all"
                >
                  <RotateCcw size={16} />
                  Reenviar com outro template
                </button>
              </div>
            )}
          </div>
        </div>
      )}

            {/* ⭐ NOVO: modal de escolha de template pro reenvio */}
      {showResendModal && selectedCampaign && (
        <ResendTemplateModal
          campaignId={selectedCampaign.id}
          recipientIds={Array.from(selectedIds)}
          onClose={() => setShowResendModal(false)}
          onResent={handleResent}
        />
      )}

      {/* ⭐ NOVO: modal de custo estimado */}
      {costData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <DollarSign size={20} className="text-green-600" />
                Custo Estimado
              </h3>
              <button onClick={() => setCostData(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">{costData.campaign_name}</p>

            {loadingCost ? (
              <div className="text-center py-8">
                <Loader2 size={24} className="animate-spin mx-auto text-gray-400" />
              </div>
            ) : (
              <>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-4 text-center">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Total estimado</p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-400">
                    US$ {(costData.total_usd || 0).toFixed(4)}
                  </p>
                  {costData.total_brl != null ? (
                    <p className="text-lg font-semibold text-green-600 dark:text-green-500 mt-1">
                      R$ {costData.total_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">Cotação indisponível no momento</p>
                  )}
                  {costData.exchange_rate != null && (
                    <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">
                      Cotação: US$ 1 = R$ {costData.exchange_rate.toFixed(4)}
                    </p>
                  )}
                </div>

                {costData.categories?.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {costData.categories.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-slate-700 rounded-lg px-3 py-2">
                        <span className="text-gray-700 dark:text-slate-200">{c.category} × {c.count}</span>
                        <span className="font-medium text-gray-900 dark:text-slate-100 text-right">
                          US$ {c.subtotal_usd.toFixed(4)}
                          {c.subtotal_brl != null && (
                            <span className="block text-xs font-normal text-gray-500 dark:text-slate-400">
                              R$ {c.subtotal_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">Nenhuma mensagem entregue ainda pra calcular.</p>
                )}

                <p className="text-[11px] text-gray-400 dark:text-slate-500 text-center">
                  {costData.note}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export default Campaigns;
